import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import { LogOut, X, RefreshCw, ChevronDown, ChevronRight, Package, Settings, HelpCircle, Clock, Truck, CheckCircle, XCircle, ShoppingBag, Store, BarChart2, Plane, Archive } from 'lucide-react';
import Expedition from '../Expedition';
import { useToast } from '../../components/Toast';
import { APP_ROUTES } from '../../utils/auth';
import { sanitize, validateForm } from '../../utils/validate';
import { syncToSheets } from '../../utils/sheetsSync';
import { extractSheetId, fetchSheetRows, colIndex } from '../../utils/sheetsImport';
import ConfirmDialog from '../../components/ConfirmDialog';
import { DEFAULT_FILTERS, applyOrderFilters } from '../../components/OrderFilters';
import Traitees from '../Traitees';
import {
  calcMerchantBilan,
  filterMerchantDeliveriesByPeriode,
  getRefDate,
  parseMoney,
  readJSON,
  STORAGE_KEYS,
} from './merchantUtils';
import { clearStoredProfileSession, getRouteForProfile, setStoredProfile } from '../../utils/session';
import { DEFAULT_DELIVERY_FEE, DELIVERY_STATUS_THEME, orderBarColor } from '../../constants/deliveryStatus';
import { sumDeliveryFees } from '../../services/deliveryFees';
import { useMerchantRuntime } from './useMerchantRuntime';
import MerchantBilanView from './MerchantBilanView';
import MerchantHeader from './MerchantHeader';
import MerchantCommandesView from './MerchantCommandesView';
import ContactHistorySheet from '../../components/ContactHistorySheet';
import MerchantNewOrderModal from './MerchantNewOrderModal';
import MerchantOrderSheet from './MerchantOrderSheet';
import MerchantProductModal from './MerchantProductModal';
import MerchantProductsView from './MerchantProductsView';
import { formatOrderTime } from '../../utils/orderTime';

const statusConfig = {
  pending:   { ...DELIVERY_STATUS_THEME.pending, icon: Clock },
  transit:   { ...DELIVERY_STATUS_THEME.transit, icon: Truck },
  delivered: { ...DELIVERY_STATUS_THEME.delivered, icon: CheckCircle },
  failed:    { ...DELIVERY_STATUS_THEME.failed, icon: XCircle },
};


export default function MerchantApp() {
  const navigate = useNavigate();
  const toast = useToast();
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [savingOrderEdit, setSavingOrderEdit] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState(null);
  const [savingSheetsConfig, setSavingSheetsConfig] = useState(false);
  const [showModal, setShowModal]     = useState(false);
  const [showMenu, setShowMenu]       = useState(false);
  const [selected, setSelected]       = useState(null);
  const [activeTab, setActiveTab]     = useState(() => localStorage.getItem(STORAGE_KEYS.tab) || 'commandes');
  const [showProductModal, setShowProductModal] = useState(false);
  const [productForm, setProductForm] = useState({ name: '', price: '', stock: '' });
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm]               = useState({ client: '', phone: '', address: '', product_id: '', product_name: '', product_price: null, product_stock: null, quantity: '1' });
  const [periode, setPeriode]         = useState(() => {
    const value = localStorage.getItem(STORAGE_KEYS.period);
    return ['jour', 'semaine', 'mois', 'custom'].includes(value) ? value : 'jour';
  });
  const [customRange, setCustomRange] = useState(() => readJSON(STORAGE_KEYS.customRange, { from: '', to: '' }));
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [bilanFilter, setBilanFilter] = useState(null); // null | 'all' | 'delivered' | 'transit' | 'pending' | 'failed'
  const [bilanDrillDate, setBilanDrillDate] = useState(null); // null | { from: Date, to: Date, label: string, subLevel?: [{from,to,label}] }
  const [confTab, setConfTab] = useState('new'); // 'new' | 'recall' | 'confirmed' | 'cancelled'
  const [merchantFilters, setMerchantFilters] = useState(() => {
    const saved = readJSON(STORAGE_KEYS.filters, null);
    if (!saved || typeof saved !== 'object') return DEFAULT_FILTERS;
    return { ...DEFAULT_FILTERS, ...saved };
  });
  const [editQtyPrice, setEditQtyPrice] = useState(null); // null | { qty, unitPrice, saving }
  const [showSheetsModal, setShowSheetsModal] = useState(false);
  const [sheetsConfig, setSheetsConfig] = useState({ url: '', col_client: 'A', col_phone: 'B', col_address: 'C', col_product: 'D', col_quantity: 'E', col_amount: 'F', col_order_number: '', has_header: true, last_row: 0, sync_interval: 5 });
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [historyOrder, setHistoryOrder] = useState(null); // historique des appels (popup)
  const sheetsConfigRef = useRef(sheetsConfig);

  const applyMerchantSheetsConfig = useCallback((config) => {
    setSheetsConfig((prev) => {
      const next = { ...prev, ...config };
      sheetsConfigRef.current = next;
      return next;
    });
  }, []);

  const {
    profile,
    deliveries,
    bilanDeliveries,
    products,
    allProfiles,
    loading,
    setDeliveries,
    fetchDeliveries,
    fetchProducts,
    refreshProfilesForPhone,
  } = useMerchantRuntime({ applyMerchantSheetsConfig });
  const productsRef = useRef(products);

  // keep refs in sync
  useEffect(() => { sheetsConfigRef.current = sheetsConfig; }, [sheetsConfig]);
  useEffect(() => { productsRef.current = products; }, [products]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.filters, JSON.stringify(merchantFilters));
  }, [merchantFilters]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.period, periode);
  }, [periode]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.customRange, JSON.stringify(customRange));
  }, [customRange]);

  // ── Gestion overlays : bloquer le scroll fond + bouton retour Android ──
  const anyOverlayOpen = showModal || showProductModal || showSheetsModal
    || bilanDrillDate !== null || bilanFilter !== null
    || selected !== null || showMenu || confirmDelete !== null || showFilterSheet
    || historyOrder !== null;

  const prevOverlayRef = useRef(false);

  const closeTopOverlay = useCallback(() => {
    if (confirmDelete)    { setConfirmDelete(null);    return; }
    if (historyOrder)     { setHistoryOrder(null);     return; }
    if (showFilterSheet)  { setShowFilterSheet(false); return; }
    if (selected)         { setSelected(null);         return; }
    if (bilanDrillDate)   { setBilanDrillDate(bilanDrillDate.parent || null); return; }
    if (bilanFilter)      { setBilanFilter(null);      return; }
    if (showSheetsModal)  { setShowSheetsModal(false); return; }
    if (showProductModal) { setShowProductModal(false);return; }
    if (showModal)        { setShowModal(false);       return; }
    if (showMenu)         { setShowMenu(false);        return; }
  }, [confirmDelete, historyOrder, showFilterSheet, selected, bilanDrillDate, bilanFilter, showSheetsModal, showProductModal, showModal, showMenu]);

  useEffect(() => {
    if (anyOverlayOpen && !prevOverlayRef.current) {
      window.history.pushState({ wakaana_overlay: true }, '');
    }
    prevOverlayRef.current = anyOverlayOpen;
  }, [anyOverlayOpen]);

  useEffect(() => {
    window.addEventListener('popstate', closeTopOverlay);
    return () => window.removeEventListener('popstate', closeTopOverlay);
  }, [closeTopOverlay]);

  // auto-sync désactivé — géré par Google Apps Script côté serveur

  const handleSubmit = async () => {
    if (submittingOrder) return;
    const errors = validateForm(form, {
      client:     { required: true, minLength: 2, maxLength: 100 },
      address:    { required: true, maxLength: 200 },
      product_id: { required: true },
    });
    if (Object.keys(errors).length) { toast(Object.values(errors)[0], 'error'); return; }
    if (!form.product_name || form.product_price === null) { toast('Veuillez sélectionner un produit', 'error'); return; }
    const qty = Math.max(1, parseInt(form.quantity) || 1);
    const { count } = await supabase.from('deliveries').select('id', { count: 'exact', head: true }).eq('store', profile.store_name);
    const orderNumber = String((count || 0) + 1).padStart(3, '0');
    const delivery = {
      order_number: orderNumber,
      client: sanitize(form.client), phone: sanitize(form.phone), address: sanitize(form.address),
      product_name: form.product_name, amount: form.product_price * qty,
      quantity: qty,
      delivery_fee: profile.fee_dakar ?? DEFAULT_DELIVERY_FEE, status: 'pending', source: 'manual', store: profile.store_name,
      confirmation_status: 'new',
    };
    setSubmittingOrder(true);
    try {
      const { error: insertError } = await supabase.from('deliveries').insert(delivery);
      if (insertError) { toast(`Erreur : ${insertError.message}`, 'error'); return; }
      syncToSheets(delivery);

      // Décrémente le stock du produit en fonction de la quantité
      if (form.product_stock !== null && form.product_stock !== undefined) {
        const newStock = Math.max(0, form.product_stock - qty);
        const { error: stockError } = await supabase.from('products').update({ stock: newStock }).eq('id', form.product_id);
        if (stockError) { toast(`Stock non mis à jour : ${stockError.message}`, 'error'); }
        fetchProducts(profile.store_name);
      }

      toast('Commande créée', 'success');
      setShowModal(false);
      setForm({ client: '', phone: '', address: '', product_id: '', product_name: '', product_price: null, product_stock: null, quantity: '1' });
      fetchDeliveries(profile.store_name);
    } finally {
      setSubmittingOrder(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editQtyPrice || !selected || savingOrderEdit) return;
    const qty    = Math.max(1, parseInt(editQtyPrice.quantity) || 1);
    const amount = parseFloat(editQtyPrice.amount);
    if (isNaN(amount) || amount < 0) { toast('Montant invalide', 'error'); return; }
    setSavingOrderEdit(true);
    const updates = {
      client:       editQtyPrice.client.trim(),
      phone:        editQtyPrice.phone.trim() || null,
      address:      editQtyPrice.address.trim(),
      product_name: editQtyPrice.product_name.trim() || null,
      quantity:     qty,
      amount,
    };
    const { error } = await supabase.from('deliveries').update(updates).eq('id', selected.id);
    if (error) { setSavingOrderEdit(false); toast(`Erreur : ${error.message}`, 'error'); return; }

    // Ajuster le stock si la quantité a changé
    const oldQty = parseInt(selected.quantity) || 1;
    const qtyDiff = qty - oldQty;
    if (qtyDiff !== 0 && selected.product_name && selected.store) {
      const { data: product } = await supabase
        .from('products')
        .select('id, stock')
        .eq('store_name', selected.store)
        .ilike('name', selected.product_name)
        .limit(1)
        .maybeSingle();
      if (product && product.stock !== null) {
        await supabase
          .from('products')
          .update({ stock: Math.max(0, product.stock - qtyDiff) })
          .eq('id', product.id);
      }
    }

    setSelected(s => ({ ...s, ...updates }));
    setDeliveries(prev => prev.map(d => d.id === selected.id ? { ...d, ...updates } : d));
    setEditQtyPrice(null);
    setSavingOrderEdit(false);
    toast('Commande mise à jour', 'success');
  };

  const handleTabChange = (tab) => {
    setShowModal(false);
    setShowProductModal(false);
    setShowSheetsModal(false);
    setBilanDrillDate(null);
    setBilanFilter(null);
    setSelected(null);
    setShowMenu(false);
    setShowFilterSheet(false);
    setConfirmDelete(null);
    setActiveTab(tab);
    localStorage.setItem(STORAGE_KEYS.tab, tab);
  };

  const handleAddProduct = async () => {
    const errors = validateForm(productForm, {
      name:  { required: true, minLength: 2, maxLength: 100 },
      price: { required: true },
    });
    if (Object.keys(errors).length) { toast(Object.values(errors)[0], 'error'); return; }
    const price = parseFloat(productForm.price);
    if (isNaN(price) || price <= 0) { toast('Prix invalide', 'error'); return; }
    const payload = {
      name: sanitize(productForm.name),
      price,
      stock: productForm.stock !== '' ? parseInt(productForm.stock) : null,
    };
    if (editingProduct) {
      const { error } = await supabase.from('products').update(payload).eq('id', editingProduct.id);
      if (error) { toast(`Erreur : ${error.message}`, 'error'); return; }
      toast(`Produit "${payload.name}" modifié`, 'success');
    } else {
      const { error } = await supabase.from('products').insert({ ...payload, store_name: profile.store_name });
      if (error) { toast(`Erreur : ${error.message}`, 'error'); return; }
      toast(`Produit "${payload.name}" ajouté`, 'success');
    }
    setProductForm({ name: '', price: '', stock: '' });
    setEditingProduct(null);
    setShowProductModal(false);
    fetchProducts(profile.store_name);
  };

  const handleDeleteProduct = async (id, name) => {
    if (deletingProductId) return;
    setDeletingProductId(id);
    const { error } = await supabase.from('products').delete().eq('id', id);
    if (error) { setDeletingProductId(null); toast(`Erreur : ${error.message}`, 'error'); return; }
    toast(`"${name}" supprimé`, 'info');
    fetchProducts(profile.store_name);
    setDeletingProductId(null);
  };


  const saveSheetsConfig = async () => {
    if (!profile || savingSheetsConfig) return;
    setSavingSheetsConfig(true);
    const { error } = await supabase.from('profiles').update({ sheets_config: sheetsConfig }).eq('id', profile.id);
    if (error) { setSavingSheetsConfig(false); toast(`Erreur : ${error.message}`, 'error'); return; }
    toast('Configuration sauvegardée', 'success');
    setSavingSheetsConfig(false);
  };

  const runSync = async (cfg) => {
    const sheetId = extractSheetId(cfg.url);
    if (!sheetId || !profile) return 0;
    try {
      const rows = await fetchSheetRows(sheetId);
      const headerOffset = cfg.has_header && cfg.last_row === 0 ? 1 : 0;
      const startRow = cfg.last_row + headerOffset;
      const newRows = rows.slice(startRow);
      if (newRows.length === 0) return 0;

      const { count: existingCount } = await supabase.from('deliveries').select('id', { count: 'exact', head: true }).eq('store', profile.store_name);
      const baseCount = existingCount || 0;
      const filteredRows = newRows.filter(row => row[colIndex(cfg.col_client)]);
      const deliveries = filteredRows.map((row, i) => {
        const productNameRaw = row[colIndex(cfg.col_product)] || null;
        const qty = parseInt(row[colIndex(cfg.col_quantity)]) || 1;
        const matchedProduct = productNameRaw
          ? productsRef.current.find(p => p.name.toLowerCase() === productNameRaw.toLowerCase().trim())
          : null;
        const amountRaw = cfg.col_amount ? (row[colIndex(cfg.col_amount)] || null) : null;
        const amountFromSheet = amountRaw ? (parseFloat(String(amountRaw).replace(/[^\d.,]/g, '').replace(',', '.')) || null) : null;
        const amount = amountFromSheet !== null ? amountFromSheet : (matchedProduct ? matchedProduct.price * qty : null);
        return {
          order_number: cfg.col_order_number ? (row[colIndex(cfg.col_order_number)] || String(baseCount + i + 1).padStart(3, '0')) : String(baseCount + i + 1).padStart(3, '0'),
          client:       row[colIndex(cfg.col_client)]   || '',
          phone:        row[colIndex(cfg.col_phone)]    || null,
          address:      row[colIndex(cfg.col_address)]  || '',
          product_name: productNameRaw,
          quantity:     qty,
          amount,
          delivery_fee: profile.fee_dakar ?? DEFAULT_DELIVERY_FEE,
          store: profile.store_name,
          source: 'shopify',
          status: 'pending',
          confirmation_status: 'new',
        };
      });

      if (deliveries.length > 0) {
        const { error: insertErr } = await supabase.from('deliveries').insert(deliveries);
        if (insertErr) throw new Error(insertErr.message);

        // Décrémente le stock des produits matchés
        const stockUpdates = {};
        filteredRows.forEach((row, i) => {
          const productNameRaw = row[colIndex(cfg.col_product)] || null;
          const qty = parseInt(row[colIndex(cfg.col_quantity)]) || 1;
          const matched = productNameRaw
            ? productsRef.current.find(p => p.name.toLowerCase() === productNameRaw.toLowerCase().trim())
            : null;
          if (matched && matched.stock !== null) {
            if (!stockUpdates[matched.id]) stockUpdates[matched.id] = { product: matched, totalQty: 0 };
            stockUpdates[matched.id].totalQty += qty;
          }
        });
        await Promise.all(
          Object.values(stockUpdates).map(({ product, totalQty }) =>
            supabase.from('products').update({ stock: Math.max(0, product.stock - totalQty) }).eq('id', product.id)
          )
        );
        if (Object.keys(stockUpdates).length > 0) fetchProducts(profile.store_name);

        const newLastRow = cfg.last_row + newRows.length;
        const updated = { ...cfg, last_row: newLastRow };
        setSheetsConfig(updated);
        sheetsConfigRef.current = updated;
        await supabase.from('profiles').update({ sheets_config: updated }).eq('id', profile.id);
        fetchDeliveries(profile.store_name);
      }
      const missingAmount = deliveries.filter(d => !d.amount).length;
      return { count: deliveries.length, missingAmount };
    } catch (err) {
      console.warn('Sheets sync error:', err.message);
      return err.message || 'Erreur inconnue';
    }
  };

  const handleSheetsSync = async () => {
    if (!extractSheetId(sheetsConfig.url)) { toast("URL de sheet invalide", 'error'); return; }
    setSyncLoading(true); setSyncResult(null);
    const result = await runSync(sheetsConfig);
    if (typeof result === 'string') toast(result, 'error');
    else setSyncResult({ imported: result.count, missingAmount: result.missingAmount });
    setSyncLoading(false);
  };

  const handleLogout = () => {
    clearStoredProfileSession();
    navigate(APP_ROUTES.landing);
  };

  const handleSwitch = (p) => {
    setStoredProfile(p);
    navigate(getRouteForProfile(p) || APP_ROUTES.home);
  };

  // Pour le bilan, la date de référence est la date de LIVRAISON (delivered_at),
  // pas la date de commande (created_at). Une commande passée la semaine dernière
  // mais livrée cette semaine apparaît dans le bilan de cette semaine uniquement.
  // Tarifs négociés de la boutique : sans eux, les frais affichés au marchand
  // ne correspondent pas à ce qu'on lui facture.
  const storeFees = { fee_dakar: profile?.fee_dakar, fee_expedition: profile?.fee_expedition };

  const bilanPeriode  = calcMerchantBilan(filterMerchantDeliveriesByPeriode(bilanDeliveries, periode, customRange), storeFees);
  const bilanJour     = calcMerchantBilan(filterMerchantDeliveriesByPeriode(bilanDeliveries, 'jour', customRange), storeFees);
  const periodeLabel = {
    jour: "Aujourd'hui",
    semaine: 'Cette semaine',
    mois: 'Ce mois',
    custom: (customRange.from && customRange.to)
      ? `${new Date(customRange.from + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })} → ${new Date(customRange.to + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}`
      : 'Période personnalisée',
  }[periode] || 'Toute période';
  const confCounts = {
    new:       deliveries.filter(d => !d.expedition && d.confirmation_status === 'new').length,
    recall:    deliveries.filter(d => !d.expedition && ['recall', 'unreachable'].includes(d.confirmation_status)).length,
    confirmed: deliveries.filter(d => !d.expedition && d.confirmation_status === 'confirmed').length,
    cancelled: deliveries.filter(d => !d.expedition && d.confirmation_status === 'cancelled').length,
  };
  const confFiltered = deliveries.filter(d => {
    if (d.expedition) return false;
    if (confTab === 'new')       return d.confirmation_status === 'new';
    if (confTab === 'recall')    return ['recall', 'unreachable'].includes(d.confirmation_status);
    if (confTab === 'confirmed') return d.confirmation_status === 'confirmed';
    if (confTab === 'cancelled') return d.confirmation_status === 'cancelled';
    return true;
  });
  const filteredMerchantOrders = applyOrderFilters(confFiltered, merchantFilters, ['client', 'phone', 'address', 'product_name', 'order_number']);
  const hasActiveMerchantFilters = Boolean(
    merchantFilters.search?.trim()
    || (merchantFilters.period && merchantFilters.period !== 'all')
    || merchantFilters.customFrom
    || merchantFilters.customTo
    || merchantFilters.driverId
    || merchantFilters.storeId
  );
  const missingPhoneCount = filteredMerchantOrders.filter(order => !String(order.phone || '').trim()).length;
  const missingAmountCount = filteredMerchantOrders.filter(order => parseMoney(order.amount) <= 0).length;

  const inputStyle = { width: '100%', padding: '11px 14px', border: '1.5px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', outline: 'none', fontFamily: 'Alan Sans, sans-serif', transition: 'border-color 0.15s', boxSizing: 'border-box' };

  const expeditionCount = bilanDeliveries.filter(d => d.expedition && !['delivered','failed'].includes(d.status)).length;

  const bottomNavTabs = [
    { id: 'commandes', label: 'Commandes', icon: Package,     count: confCounts.new || null },
    { id: 'produits',  label: 'Produits',  icon: ShoppingBag, count: null },
    { id: 'bilan',     label: 'Bilan',     icon: BarChart2,   count: null },
  ];

  return (
    <div style={{ height: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#f0f3f8' }}>

      {/* ── HEADER ──────────────────────────────────────────── */}
      <MerchantHeader
        activeTab={activeTab}
        onOpenMenu={async () => {
          setShowMenu(true);
          if (profile?.phone) await refreshProfilesForPhone(profile.phone, profile.id);
        }}
        onOpenNewOrder={() => {
          setForm({ client: '', phone: '', address: '', product_id: '', product_name: '', product_price: null, product_stock: null, quantity: '1' });
          setShowModal(true);
        }}
        onOpenAddProduct={() => setShowProductModal(true)}
      />

      {/* ── SIDE MENU ───────────────────────────────────────── */}
      {showMenu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
            onClick={() => setShowMenu(false)} />
          <div style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '272px', zIndex: 50, background: 'linear-gradient(180deg, #07162d 0%, #0d1e3d 50%, #112040 100%)', display: 'flex', flexDirection: 'column', boxShadow: '4px 0 32px rgba(0,0,0,0.35)', borderRight: '1px solid rgba(255,255,255,0.05)' }}>

            {/* Header menu */}
            <div style={{ padding: '18px 16px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '11px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ShoppingBag size={17} color="white" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profile?.store_name}</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '1px' }}>E-commerçant</div>
              </div>
              <button onClick={() => setShowMenu(false)}
                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', cursor: 'pointer', color: 'rgba(255,255,255,0.7)', padding: '7px', borderRadius: '8px', display: 'flex', flexShrink: 0 }}>
                <X size={15} />
              </button>
            </div>

            {/* Corps */}
            <div style={{ flex: 1, padding: '14px 12px', overflowY: 'auto' }}>

              {/* Changer de compte */}
              {allProfiles.filter(p => p.id !== profile?.id).length > 0 && (
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '2px', fontWeight: 700, marginBottom: '8px', paddingLeft: '4px' }}>Changer de compte</div>
                  {allProfiles.filter(p => p.id !== profile?.id).map(p => (
                    <button key={p.id} onClick={() => handleSwitch(p)}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 11px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.05)', cursor: 'pointer', textAlign: 'left', marginBottom: '5px' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}>
                      <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <RefreshCw size={13} color="rgba(255,255,255,0.7)" />
                      </div>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#ffffff' }}>{p.role === 'merchant' ? p.store_name : p.name}</div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{p.role === 'merchant' ? 'E-commerçant' : p.role === 'gestionnaire' ? 'Gestionnaire' : p.role === 'driver' ? 'Livreur' : 'Particulier'}</div>
                      </div>
                    </button>
                  ))}
                  <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', margin: '10px 0' }} />
                </div>
              )}

              {/* Navigation */}
              {[
                { id: 'traitees',   icon: Archive,  label: 'Traitées',    sub: 'Commandes livrées & archivées' },
                { id: 'expedition', icon: Plane,     label: 'Expédition',  sub: 'Livraisons hors Dakar', badge: expeditionCount },
              ].map(({ id, icon: Icon, label, sub, badge }) => {
                const active = activeTab === id;
                return (
                  <button key={id} onClick={() => { handleTabChange(id); setShowMenu(false); }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 11px', borderRadius: '10px', border: `1px solid ${active ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.07)'}`, background: active ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.04)', cursor: 'pointer', textAlign: 'left', marginBottom: '5px' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                    onMouseLeave={e => e.currentTarget.style.background = active ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.04)'}>
                    <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: active ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, position: 'relative' }}>
                      <Icon size={14} color="white" />
                      {badge > 0 && (
                        <div style={{ position: 'absolute', top: '-4px', right: '-4px', width: '14px', height: '14px', borderRadius: '50%', background: '#c53a2d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', fontWeight: 800, color: 'white' }}>
                          {badge}
                        </div>
                      )}
                    </div>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: active ? '#ffffff' : 'rgba(255,255,255,0.8)' }}>{label}</div>
                      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>{sub}</div>
                    </div>
                  </button>
                );
              })}

              <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)', margin: '10px 0' }} />

              <button onClick={() => { setShowMenu(false); setShowSheetsModal(true); }}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 11px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.04)', cursor: 'pointer', textAlign: 'left', marginBottom: '5px' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}>
                <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Settings size={14} color="white" />
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Intégration Shopify</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>Importer depuis Google Sheets</div>
                </div>
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 11px', borderRadius: '10px', opacity: 0.35 }}>
                <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <HelpCircle size={14} color="white" />
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>Aide & Support</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Bientôt disponible</div>
                </div>
              </div>
            </div>

            {/* Déconnexion */}
            <div style={{ padding: '14px 12px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
              <button onClick={handleLogout}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 14px', borderRadius: '10px', border: '1px solid rgba(197,58,45,0.4)', background: 'rgba(197,58,45,0.12)', cursor: 'pointer', color: '#f87171', fontSize: '13px', fontWeight: 600 }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(197,58,45,0.22)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(197,58,45,0.12)'}>
                <LogOut size={15} /> Se déconnecter
              </button>
            </div>
          </div>
        </>
      )}

      {/* La largeur est bornée : sans cela la mise en page pensée pour le
          téléphone s'étire sur tout l'écran, et le montant d'une commande se
          retrouve à l'opposé du nom du client. */}
      {/* Le bandeau étant fixé, il ne pousse plus le contenu : on réserve sa
          hauteur ici, comme le fait <main> côté admin. */}
      <div style={{ flex: 1, overflowY: anyOverlayOpen ? 'hidden' : 'auto', padding: '14px 16px', paddingTop: '78px', paddingBottom: '86px' }}>
       <div style={{ width: '100%', maxWidth: '1100px', margin: '0 auto' }}>

        {/* Titre de l'onglet courant. Il reprend le libellé du menu du bas :
            une seule source, donc aucun risque que les deux divergent. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '14px' }}>
          <h1 className="ui-title">
            {bottomNavTabs.find((tab) => tab.id === activeTab)?.label}
          </h1>
          {/* Un compte à plusieurs boutiques doit voir laquelle il consulte, et
              pouvoir en changer sans ouvrir le menu. */}
          {allProfiles.filter(p => p.role === 'merchant').length > 1 && profile?.store_name && (
            <button
              onClick={async () => {
                setShowMenu(true);
                if (profile?.phone) await refreshProfilesForPhone(profile.phone, profile.id);
              }}
              title="Changer de boutique"
              style={{
                display: 'flex', alignItems: 'center', gap: '5px', flexShrink: 0,
                maxWidth: '55%', padding: '5px 10px', borderRadius: '20px',
                border: '1.5px solid #c7d7ef', background: '#eef4ff',
                color: '#173b78', fontSize: '12px', fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <Store size={12} style={{ flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{profile.store_name}</span>
              <ChevronDown size={12} style={{ flexShrink: 0, opacity: 0.7 }} />
            </button>
          )}
        </div>

        {activeTab === 'commandes' && (
          <MerchantCommandesView
            loading={loading}
            deliveries={deliveries}
            bilanJour={bilanJour}
            confTab={confTab}
            confCounts={confCounts}
            merchantFilters={merchantFilters}
            showFilterSheet={showFilterSheet}
            filteredMerchantOrders={filteredMerchantOrders}
            missingPhoneCount={missingPhoneCount}
            missingAmountCount={missingAmountCount}
            hasActiveMerchantFilters={hasActiveMerchantFilters}
            statusConfig={statusConfig}
            selectedId={selected?.id || null}
            setConfTab={setConfTab}
            setMerchantFilters={setMerchantFilters}
            setShowFilterSheet={setShowFilterSheet}
            onCreateOrder={() => {
              setForm({ client: '', phone: '', address: '', product_id: '', product_name: '', product_price: null, product_stock: null, quantity: '1' });
              setShowModal(true);
            }}
            onSelectOrder={setSelected}
            onOpenHistory={setHistoryOrder}
          />
        )}

        {/* ── PRODUITS ────────────────────────────────────── */}
        {activeTab === 'produits' && (
          <MerchantProductsView
            products={products}
            deletingProductId={deletingProductId}
            onOpenAddProduct={() => setShowProductModal(true)}
            onEditProduct={(product) => {
              setEditingProduct(product);
              setProductForm({
                name: product.name,
                price: String(product.price),
                stock:
                  product.stock !== null && product.stock !== undefined
                    ? String(product.stock)
                    : '',
              });
              setShowProductModal(true);
            }}
            onRequestDelete={(product) => setConfirmDelete({ id: product.id, name: product.name })}
          />
        )}

        {/* ── EXPÉDITION ──────────────────────────────────── */}
        {activeTab === 'expedition' && profile && (
          <Expedition storeFilter={profile.store_name} onBack={() => handleTabChange('commandes')} />
        )}

        {/* ── TRAITÉES ────────────────────────────────────── */}
        {activeTab === 'traitees' && profile && (
          <Traitees storeName={profile.store_name} onBack={() => handleTabChange('commandes')} />
        )}

        {/* ── BILAN ───────────────────────────────────────── */}
        {activeTab === 'bilan' && (
          <MerchantBilanView
            bilanPeriode={bilanPeriode}
            bilanDeliveries={bilanDeliveries}
            periode={periode}
            setPeriode={setPeriode}
            customRange={customRange}
            setCustomRange={setCustomRange}
            periodeLabel={periodeLabel}
            getRefDate={getRefDate}
            setBilanFilter={setBilanFilter}
            setBilanDrillDate={setBilanDrillDate}
            storeFees={storeFees}
          />
        )}
       </div>
      </div>

      {/* ── BOTTOM NAVIGATION ───────────────────────────── */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'white', borderTop: '1px solid #eef0f6', display: 'flex', zIndex: 30, boxShadow: '0 -4px 20px rgba(19,40,72,0.08)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {bottomNavTabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => handleTabChange(tab.id)}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '10px 0 12px', border: 'none', cursor: 'pointer', background: 'transparent', gap: '4px', color: isActive ? '#173b78' : '#94a3b8', transition: 'color 0.15s', position: 'relative' }}>
              {isActive && <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '28px', height: '2.5px', background: '#173b78', borderRadius: '0 0 3px 3px' }} />}
              <div style={{ position: 'relative' }}>
                <Icon size={21} strokeWidth={isActive ? 2.5 : 1.8} />
                {tab.count !== null && tab.count > 0 && !isActive && (
                  <div style={{ position: 'absolute', top: '-4px', right: '-6px', width: '14px', height: '14px', borderRadius: '50%', background: '#c53a2d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '8px', fontWeight: 800, color: 'white' }}>
                    {tab.count > 9 ? '9+' : tab.count}
                  </div>
                )}
              </div>
              <span style={{ fontSize: '10px', fontWeight: isActive ? 700 : 500 }}>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── BOTTOM SHEET : DRILL-DOWN DATE ──────────────── */}
      {bilanDrillDate && (() => {
        const { from, to, label, subDays, parent } = bilanDrillDate;
        // Fermer un niveau remonte au précédent plutôt que de tout refermer :
        // descendre mois → semaine → jour puis ressortir d'un cran.
        const closeDrill = () => setBilanDrillDate(parent || null);
        const orders = bilanDeliveries.filter(d => {
          if (d.status !== 'delivered') return false;
          const dt = getRefDate(d);
          return dt >= from && dt <= to;
        });
        const ca  = orders.reduce((s, d) => s + parseMoney(d.amount), 0);
        const net = ca - sumDeliveryFees(orders, storeFees);

        // Si subDays → afficher les jours de la semaine (niveau 2 depuis mois)
        if (subDays) {
          const getDayRange2 = (d) => {
            const from2 = new Date(d); from2.setHours(0, 0, 0, 0);
            const to2   = new Date(d); to2.setHours(23, 59, 59, 999);
            return { from: from2, to: to2 };
          };
          const dayLabel2 = (d) => {
            const now  = new Date();
            const yest = new Date(now); yest.setDate(now.getDate() - 1);
            if (d.toDateString() === now.toDateString())  return "Aujourd'hui";
            if (d.toDateString() === yest.toDateString()) return 'Hier';
            return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
          };
          return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'flex-end', background: 'rgba(10,18,40,0.55)', backdropFilter: 'blur(4px)' }}
              onClick={closeDrill}>
              <div style={{ background: '#f8fafc', borderRadius: '24px 24px 0 0', width: '100%', maxWidth: '680px', margin: '0 auto', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 -24px 80px rgba(0,0,0,0.2)' }}
                onClick={e => e.stopPropagation()}>
                <div style={{ padding: '16px 0 0', display: 'flex', justifyContent: 'center' }}>
                  <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#e5e7eb' }} />
                </div>
                <div style={{ padding: '16px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '17px', fontWeight: 800, color: '#0f172a' }}>{label}</div>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>{orders.length} livraison{orders.length !== 1 ? 's' : ''} · {ca.toLocaleString('fr-FR')} F</div>
                  </div>
                  <button onClick={closeDrill}
                    style={{ width: '32px', height: '32px', borderRadius: '9px', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <X size={15} color="#9ca3af" />
                  </button>
                </div>
                <div style={{ padding: '0 12px 24px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {subDays.map((day, i) => {
                    const { from: f2, to: t2 } = getDayRange2(day);
                    const dayOrders = orders.filter(d => { const dt = getRefDate(d); return dt >= f2 && dt <= t2; });
                    const dayCa  = dayOrders.reduce((s, d) => s + parseMoney(d.amount), 0);
                    const dayNet = dayCa - sumDeliveryFees(dayOrders, storeFees);
                    const isToday = day.toDateString() === new Date().toDateString();
                    const isFuture = day > new Date();
                    return (
                      <div key={i}
                        onClick={() => dayOrders.length > 0 && setBilanDrillDate({ from: f2, to: t2, label: dayLabel2(day), parent: bilanDrillDate })}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '10px', background: isToday ? '#f0f4ff' : 'white', border: `1px solid ${isToday ? '#bfdbfe' : '#eef0f6'}`, cursor: dayOrders.length > 0 ? 'pointer' : 'default', opacity: isFuture ? 0.35 : 1 }}>
                        <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: isToday ? '#173b78' : '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: '12px', fontWeight: 800, color: isToday ? 'white' : '#64748b' }}>{day.getDate()}</span>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '13px', fontWeight: isToday ? 700 : 500, color: '#0f172a' }}>{dayLabel2(day)}</div>
                          {dayOrders.length > 0 && <div style={{ fontSize: '11px', color: '#64748b' }}>{dayOrders.length} livraison{dayOrders.length > 1 ? 's' : ''}</div>}
                          {dayOrders.length === 0 && !isFuture && <div style={{ fontSize: '11px', color: '#d1d5db' }}>Aucune livraison</div>}
                        </div>
                        {dayOrders.length > 0 && (
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: '13px', fontWeight: 800, color: '#173b78' }}>{dayCa.toLocaleString('fr-FR')} F</div>
                            <div style={{ fontSize: '10px', color: '#4ade80', fontWeight: 700 }}>+{dayNet.toLocaleString('fr-FR')} net</div>
                          </div>
                        )}
                        {dayOrders.length > 0 && <ChevronRight size={14} color="#94a3b8" style={{ flexShrink: 0 }} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        }

        // Sinon → liste des commandes du jour/période
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'flex-end', background: 'rgba(10,18,40,0.55)', backdropFilter: 'blur(4px)' }}
            onClick={closeDrill}>
            <div style={{ background: '#f8fafc', borderRadius: '24px 24px 0 0', width: '100%', maxWidth: '680px', margin: '0 auto', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 -24px 80px rgba(0,0,0,0.2)' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ padding: '16px 0 0', display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#e5e7eb' }} />
              </div>
              <div style={{ padding: '16px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '17px', fontWeight: 800, color: '#0f172a' }}>{label}</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>{orders.length} livraison{orders.length !== 1 ? 's' : ''} · <span style={{ color: '#4ade80', fontWeight: 700 }}>+{net.toLocaleString('fr-FR')} net</span></div>
                </div>
                <button onClick={closeDrill}
                  style={{ width: '32px', height: '32px', borderRadius: '9px', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={15} color="#9ca3af" />
                </button>
              </div>
              <div style={{ padding: '0 12px 24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {orders.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', fontSize: '13px', color: '#94a3b8' }}>Aucune commande</div>
                ) : orders.map(d => {
                  const s = statusConfig[d.status] || statusConfig.pending;
                  return (
                    <div key={d.id} onClick={() => setSelected(d)}
                      style={{ background: 'white', borderRadius: '14px', border: `1.5px solid ${s.border}`, padding: '13px 14px', cursor: 'pointer', display: 'flex', gap: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                      <div style={{ width: '4px', background: orderBarColor(d), flexShrink: 0, borderRadius: '3px' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                          {d.order_number && <span style={{ fontSize: '11px', fontWeight: 700, color: '#173b78', fontFamily: 'monospace', background: '#eff6ff', padding: '1px 6px', borderRadius: '5px' }}>#{d.order_number}</span>}
                          <span style={{ fontWeight: 700, fontSize: '14px', color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.client || '—'}</span>
                        </div>
                        {d.product_name && <div style={{ fontSize: '11px', color: '#2a66b8', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.product_name}{d.quantity > 1 ? ` ×${d.quantity}` : ''}</div>}
                      </div>
                      <div style={{ flexShrink: 0, textAlign: 'right' }}>
                        {d.amount && <div style={{ fontSize: '13px', fontWeight: 800, color: '#173b78' }}>{Number(d.amount).toLocaleString('fr-FR')} F</div>}
                        <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>
                          {formatOrderTime(getRefDate(d))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── BOTTOM SHEET : LISTE BILAN FILTRÉE ──────────── */}
      {bilanFilter && (() => {
        const periodDeliveries = filterMerchantDeliveriesByPeriode(bilanDeliveries, periode, customRange);
        const filtered = bilanFilter === 'all' ? periodDeliveries : periodDeliveries.filter(d => d.status === bilanFilter);
        const label = { all: 'Toutes', delivered: 'Livrées', transit: 'En transit', pending: 'En attente', failed: 'Retours' }[bilanFilter];
        return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'flex-end', background: 'rgba(10,18,40,0.55)', backdropFilter: 'blur(4px)' }}
            onClick={() => setBilanFilter(null)}>
            <div style={{ background: '#f8fafc', borderRadius: '24px 24px 0 0', width: '100%', maxWidth: '680px', margin: '0 auto', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 -24px 80px rgba(0,0,0,0.2)' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ padding: '16px 0 0', display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#e5e7eb' }} />
              </div>
              <div style={{ padding: '16px 20px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '17px', fontWeight: 800, color: '#0f172a' }}>{label}</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>{filtered.length} commande{filtered.length !== 1 ? 's' : ''}</div>
                </div>
                <button onClick={() => setBilanFilter(null)}
                  style={{ width: '32px', height: '32px', borderRadius: '9px', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={15} color="#9ca3af" />
                </button>
              </div>
              <div style={{ padding: '0 12px 24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {filtered.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0', fontSize: '13px', color: '#94a3b8' }}>Aucune commande</div>
                ) : filtered.map(d => {
                  const s = statusConfig[d.status] || statusConfig.pending;
                  const Icon = s.icon;
                  return (
                    <div key={d.id} onClick={() => { setBilanFilter(null); setSelected(d); }}
                      style={{ background: 'white', borderRadius: '14px', border: `1.5px solid ${s.border}`, padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '11px', background: s.bg, border: `1px solid ${s.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon size={16} color={s.color} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 800, color: '#173b78', fontFamily: 'monospace' }}>{d.order_number ? `#${d.order_number}` : d.id}</span>
                          <span style={{ padding: '3px 9px', borderRadius: '7px', fontSize: '11px', fontWeight: 800, background: s.bg, color: s.color, border: `1.5px solid ${s.border}` }}>{s.label}</span>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a', marginBottom: '2px' }}>{d.client}</div>
                        {d.product_name && <div style={{ fontSize: '11px', fontWeight: 600, color: '#1d4ed8' }}>{d.product_name}{d.quantity > 1 ? ` ×${d.quantity}` : ''}</div>}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                        {d.amount && <div style={{ fontSize: '13px', fontWeight: 700, color: '#173b78' }}>{Number(d.amount).toLocaleString('fr-FR')} F</div>}
                        <div style={{ fontSize: '10px', color: '#94a3b8' }}>{new Date(d.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</div>
                        <ChevronRight size={14} color="#d1d5db" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {selected && (
        <MerchantOrderSheet
          selected={selected}
          storeFees={storeFees}
          products={products}
          editQtyPrice={editQtyPrice}
          savingOrderEdit={savingOrderEdit}
          statusConfig={statusConfig}
          setEditQtyPrice={setEditQtyPrice}
          onClose={() => {
            setSelected(null);
            setEditQtyPrice(null);
          }}
          onSaveEdit={handleSaveEdit}
        />
      )}

      {/* ── MODAL NOUVELLE COMMANDE ─────────────────────── */}
      {showModal && (
        <MerchantNewOrderModal
          form={form}
          products={products}
          inputStyle={inputStyle}
          submittingOrder={submittingOrder}
          deliveryFee={profile?.fee_dakar ?? DEFAULT_DELIVERY_FEE}
          setForm={setForm}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
        />
      )}

      {/* ── MODAL AJOUT PRODUIT ─────────────────────────── */}
      {showProductModal && (
        <MerchantProductModal
          editingProduct={editingProduct}
          productForm={productForm}
          inputStyle={inputStyle}
          setProductForm={setProductForm}
          onClose={() => {
            setShowProductModal(false);
            setEditingProduct(null);
            setProductForm({ name: '', price: '', stock: '' });
          }}
          onSubmit={handleAddProduct}
        />
      )}

      {/* ── MODAL INTÉGRATION SHEETS ────────────────────── */}
      {showSheetsModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(10,18,40,0.55)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowSheetsModal(false)}>
          <div style={{ background: 'white', borderRadius: '24px 24px 0 0', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 -24px 80px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ padding: '16px 0 0', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: '40px', height: '4px', borderRadius: '2px', background: '#e5e7eb' }} />
            </div>
            <div style={{ padding: '16px 24px 32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                <div className="ui-sheet-title" style={{ fontSize: '18px' }}>Intégration Shopify</div>
                <button onClick={() => setShowSheetsModal(false)}
                  style={{ width: '30px', height: '30px', borderRadius: '8px', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={14} color="#64748b" />
                </button>
              </div>
              <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '20px', lineHeight: 1.6 }}>
                Colle l'URL de ton Google Sheet connecté à Shopify. Le sheet doit être <strong style={{ color: '#173b78' }}>publié sur le web</strong> (Fichier → Partager → Publier sur le web).
              </p>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>URL Google Sheet</label>
                <input value={sheetsConfig.url} onChange={e => setSheetsConfig(p => ({ ...p, url: e.target.value }))}
                  style={inputStyle} placeholder="https://docs.google.com/spreadsheets/d/..." />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Colonnes (lettre A, B, C…)</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {[
                    { key: 'col_client',       label: 'Nom client' },
                    { key: 'col_phone',        label: 'Téléphone' },
                    { key: 'col_address',      label: 'Adresse' },
                    { key: 'col_product',      label: 'Produit' },
                    { key: 'col_quantity',     label: 'Quantité' },
                    { key: 'col_amount',       label: 'Prix (montant)' },
                    { key: 'col_order_number', label: 'N° commande' },
                  ].map(({ key, label }) => (
                    <div key={key}>
                      <label style={{ display: 'block', fontSize: '10px', color: '#94a3b8', marginBottom: '4px' }}>{label}{key === 'col_order_number' && <span style={{ color: '#c2410c' }}> (optionnel)</span>}</label>
                      <input value={sheetsConfig[key]}
                        onChange={e => setSheetsConfig(p => ({ ...p, [key]: e.target.value.toUpperCase().slice(0, 1) }))}
                        maxLength={1} style={{ ...inputStyle, textAlign: 'center', fontWeight: 700, fontSize: '16px', padding: '10px' }}
                        placeholder="—" />
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px' }}>Synchro automatique</label>
                <select value={sheetsConfig.sync_interval}
                  onChange={e => setSheetsConfig(p => ({ ...p, sync_interval: Number(e.target.value) }))}
                  style={inputStyle}>
                  <option value={5}>Toutes les 5 minutes</option>
                  <option value={10}>Toutes les 10 minutes</option>
                  <option value={15}>Toutes les 15 minutes</option>
                  <option value={30}>Toutes les 30 minutes</option>
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', padding: '12px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                <input type="checkbox" id="has_header" checked={sheetsConfig.has_header}
                  onChange={e => setSheetsConfig(p => ({ ...p, has_header: e.target.checked }))}
                  style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                <label htmlFor="has_header" style={{ fontSize: '13px', color: '#374151', fontWeight: 500, cursor: 'pointer' }}>
                  La première ligne est un en-tête (à ignorer)
                </label>
              </div>

              {syncResult !== null && (
                <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ padding: '12px 14px', borderRadius: '10px', background: syncResult.imported > 0 ? '#f0fdf4' : '#f8fafc', border: `1px solid ${syncResult.imported > 0 ? '#bbf7d0' : '#e2e8f0'}`, fontSize: '13px', fontWeight: 600, color: syncResult.imported > 0 ? '#15803d' : '#64748b' }}>
                    {syncResult.imported > 0 ? `✓ ${syncResult.imported} commande${syncResult.imported > 1 ? 's' : ''} importée${syncResult.imported > 1 ? 's' : ''}` : 'Aucune nouvelle commande trouvée'}
                  </div>
                  {syncResult.missingAmount > 0 && (
                    <div style={{ padding: '10px 14px', borderRadius: '10px', background: '#fffbeb', border: '1px solid #fde68a', fontSize: '12px', fontWeight: 600, color: '#92400e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      ⚠ {syncResult.missingAmount} commande{syncResult.missingAmount > 1 ? 's' : ''} sans montant — produit non trouvé dans le catalogue
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={saveSheetsConfig} disabled={savingSheetsConfig}
                  style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1.5px solid #173b78', background: 'white', color: '#173b78', opacity: savingSheetsConfig ? 0.6 : 1, fontSize: '13px', fontWeight: 600, cursor: savingSheetsConfig ? 'not-allowed' : 'pointer' }}>
                  {savingSheetsConfig ? 'Sauvegarde…' : 'Sauvegarder'}
                </button>
                <button onClick={handleSheetsSync} disabled={!sheetsConfig.url || syncLoading}
                  style={{ flex: 2, padding: '12px', borderRadius: '10px', border: 'none', background: !sheetsConfig.url || syncLoading ? '#e5e7eb' : 'linear-gradient(135deg, #173b78, #2a66b8)', color: !sheetsConfig.url || syncLoading ? '#9ca3af' : 'white', fontSize: '13px', fontWeight: 600, cursor: !sheetsConfig.url || syncLoading ? 'not-allowed' : 'pointer' }}>
                  {syncLoading ? 'Synchronisation…' : '↓ Importer nouvelles commandes'}
                </button>
              </div>

              {sheetsConfig.last_row > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px', padding: '8px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                    {sheetsConfig.last_row} ligne{sheetsConfig.last_row > 1 ? 's' : ''} déjà importée{sheetsConfig.last_row > 1 ? 's' : ''}
                  </span>
                  <button onClick={async () => {
                    const updated = { ...sheetsConfig, last_row: 0 };
                    setSheetsConfig(updated);
                    sheetsConfigRef.current = updated;
                    await supabase.from('profiles').update({ sheets_config: updated }).eq('id', profile.id);
                    setSyncResult(null);
                    toast('Compteur réinitialisé — toutes les lignes seront relues', 'info');
                  }} style={{ fontSize: '11px', fontWeight: 600, color: '#c2410c', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px', borderRadius: '6px' }}>
                    Réinitialiser
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {historyOrder && (
        <ContactHistorySheet delivery={historyOrder} onClose={() => setHistoryOrder(null)} />
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Supprimer le produit"
        message={`Supprimer définitivement "${confirmDelete?.name}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        onConfirm={() => { handleDeleteProduct(confirmDelete.id, confirmDelete.name); setConfirmDelete(null); }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
